/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Center,
    Tooltip,
    Flex,
    useMultiStyleConfig,
    omitThemingProps,
    useBreakpointValue,
    Button,
    useRadio,
    VisuallyHidden
} from '@chakra-ui/react'

/**
 * The Swatch Component displays item inside `SwatchGroup`
 */
const Swatch = (props) => {
    const styles = useMultiStyleConfig('SwatchGroup', props)
    const {
        disabled,
        selected,
        orderable,
        htmlName,
        label,
        children,
        href,
        onChange,
        value,
        name,
        propagateDisabled,
        tooltipPlacement,
        onHoverChange,
        ...ownProps
    } = omitThemingProps(props)
    const hideTooltip = useBreakpointValue({base: true, lg: false})
    const {getInputProps, getLabelProps, htmlProps, getRadioProps} = useRadio({
        id: `${htmlName}-${value}`,
        name: htmlName,
        value: value,
        'aria-label': name,
        isChecked: selected,
        isDisabled: propagateDisabled && disabled,
        onChange: (e) => {
            //this is needed to open the quickshopmodal when clicking on the +x color-swatch
            if (!!value && !!href) {
                e.stopPropagation()
                onChange(value, href)
                onHoverChange?.()
            }
        }
    })

    return (
        <Flex as="label" alignItems="flex-start" cursor="pointer" {...htmlProps}>
            <input {...getInputProps()} />
            <Tooltip
                hasArrow
                label={name}
                isDisabled={hideTooltip}
                {...(tooltipPlacement && {placement: tooltipPlacement})}
            >
                <Button
                    as="div"
                    {...getRadioProps()}
                    {...styles.swatch}
                    data-testid="swatch"
                    variant="swatch"
                    {...ownProps}
                >
                    <Center {...styles.swatchButton}>
                        {children}
                        {label && <Box {...styles.label}>{label}</Box>}
                    </Center>
                </Button>
            </Tooltip>
            <VisuallyHidden {...getLabelProps()}>{name}</VisuallyHidden>
        </Flex>
    )
}

Swatch.displayName = 'Swatch'

Swatch.propTypes = {
    /**
     * Size of the swatches
     */
    size: PropTypes.string,
    /**
     * The children to be rendered within swatch item.
     */
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
    /**
     * Indicates whether the option is disabled
     */
    disabled: PropTypes.bool,
    /**
     * Indicates whether the option is orderable
     */
    orderable: PropTypes.bool,
    /**
     * Indicates whether the option is selected.
     * This props is provided internally by SwatchGroup
     */
    selected: PropTypes.bool,
    /**
     * The shape of the Swatch
     */
    variant: PropTypes.oneOf(['square', 'circle', 'chip']),
    /**
     * The label of the option.
     */
    label: PropTypes.string,
    /**
     *  The url of this option
     */
    href: PropTypes.string,
    /**
     * This function is called whenever the user selects an option.
     * It is passed the new value.
     */
    onChange: PropTypes.func,
    /**
     * The value for the option.
     */
    value: PropTypes.string,
    /**
     * The display value for each swatch
     */
    name: PropTypes.string,
    /**
     * Indicates whether the disabled attribute should be
     * propagated to the inactive color swatches
     */
    propagateDisabled: PropTypes.bool,
    /**
     * Position of the tooltip info to display for swatch
     */
    tooltipPlacement: PropTypes.string,
    /**
     * This function is called when cursor is over or move out of a color value
     */
    onHoverChange: PropTypes.func
}

export default Swatch
